<template>
  <div class="meeting-container" v-if="meeting">
    <div class="meeting-layout-top">
      <div class="meeting-top-container">
        <div class="meeting-top-logo">
          <img src="../assets/images/p-logo.svg" @click="goToOrganization" />
        </div>

        <div class="meeting-top-back">
          <v-btn class="white--text" style="pointer-events: none" icon>
            <v-icon>mdi-home-city</v-icon>
          </v-btn>
        </div>

        <div class="meeting-top-left">
          <h3 class="white--text">
            {{ meeting.organizationName }} - {{ meeting.name }}
          </h3>
        </div>
      </div>
    </div>

    <agenda :meeting="meeting" :isOpen="isOpen"></agenda>

    <div class="meeting-middle-top row-container">
      <div class="row-left">
        <h2 class="white--text">Beslutspunkter</h2>
      </div>
    </div>

    <div class="meeting-middle-content" v-if="decisionPoints.length > 0">
      <v-card
        elevation="1"
        style="margin-bottom: 12px;margin-right: 4px;"
        class="background--color-c"
        v-for="point in decisionPoints"
        :key="point.id"
      >
        <v-card
          v-on:click="toggleVisible(point.id)"
          style="user-select: none;"
          :class="[point.closed ? 'background--transparent' : '']"
        >
          <div class="decision-container">
            <div class="decision-icon">
              <v-icon style="padding-right: 5px">mdi-vote</v-icon>
            </div>
            <div class="decision-text subtitle-2">
              {{ point.text }}
            </div>
            <div class="decision-edit"></div>
            <div class="decision-unfold"></div>
          </div>
        </v-card>
        <v-card light class="background--color-c">
          <v-row>
            <v-col v-if="point.results">
              <v-card-text style="padding-top: 0">
                <v-simple-table>
                  <thead>
                    <tr>
                      <th class="text-left">Alternativ</th>
                      <th class="text-left">Resultat</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="result in sortedResults(
                        point.results ? point.results : point.preliminaryResult
                      )"
                      :key="result.id"
                    >
                      <td>{{ translateResult(result.vote) }}</td>
                      <td>{{ result.result }}</td>
                    </tr>
                  </tbody>
                </v-simple-table>
              </v-card-text>
            </v-col>
            <v-col v-else-if="point.alternatives">
              <v-card-text style="padding-top: 0">
                <v-simple-table>
                  <thead>
                    <tr>
                      <th class="text-left">Alternativ</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="alt in point.alternatives" :key="alt.id">
                      <td>{{ alt }}</td>
                    </tr>
                  </tbody>
                </v-simple-table>
              </v-card-text>
            </v-col>
            <v-col v-else>
              <v-card-text style="padding-top: 0">
                <v-simple-table>
                  <thead>
                    <tr>
                      <th class="text-left">Alternativ</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>Ja</td>
                    </tr>
                    <tr>
                      <td>Nej</td>
                    </tr>
                  </tbody>
                </v-simple-table>
              </v-card-text>
            </v-col>
          </v-row>
        </v-card>
      </v-card>
    </div>
    <div class="meeting-middle-content" v-else>
      <div class="white--text">Inga beslutspunkter har röstats igenom.</div>
    </div>

    <div class="meeting-right" style="width: 350px;">
      <div
        class="d-flex flex-column"
        style="flex-grow: 0; flex-shrink: 0; height:100%;"
        v-bind:style="{ width: ballotWidth }"
      >
        <h2 class="white--text mb-5">Röstlängd</h2>
        <div class="overflow-y-auto overflow-x-hidden mb-8">
          <v-simple-table light>
            <tbody>
              <tr>
                <td>{{ totalNumberOfBallots }}</td>
                <td style="width:100%">Rösträtter</td>
              </tr>
              <tr>
                <td>{{ totalNumberOfPresentBallots }}</td>
                <td>Närvarande rösträtter (inkl. fullmakter)</td>
              </tr>
              <tr>
                <td>{{ totalNumberOfPresentMandatedBallots }}</td>
                <td>Fullmakter</td>
              </tr>
              <tr>
                <td>{{ totalNumberOfMembers }}</td>
                <td>Medlemmar</td>
              </tr>
              <tr>
                <td>{{ totalNumberOfPresentMembers }}</td>
                <td>Närvarande medlemmar</td>
              </tr>
            </tbody>
          </v-simple-table>
        </div>

        <speaker-time
          v-if="state === 3"
          class="mb-4"
          title="Har ordet"
          :activeSpeakerId="activeSpeakerId.speakerId"
          :isActive="state === 3 && !!activeSpeakerId.speakerId"
        ></speaker-time>
        <div v-if="state === 3">
          <div class="overflow-y-auto overflow-x-hidden mb-5">
            <v-simple-table light>
              <tbody>
                <tr>
                  <td class="active-speaker-name" style="width:100%">
                    {{ activeSpeakerDisplayName }}
                  </td>
                  <td>
                    <v-icon
                      v-if="hasActiveSpeaker"
                      small
                      style="margin-right:5px"
                      color="success"
                    >
                      mdi-account-voice
                    </v-icon>
                  </td>
                </tr>
              </tbody>
            </v-simple-table>
          </div>

          <h2 class="white--text mb-5">Talarkö</h2>
          <div
            class="overflow-y-auto overflow-x-hidden"
            v-if="speakers.length > 0"
          >
            <v-simple-table light>
              <tbody>
                <tr v-for="elector in speakers" :key="elector.id">
                  <td style="width:100%">{{ elector.name }}</td>
                </tr>
              </tbody>
            </v-simple-table>
          </div>
          <div class="overflow-y-auto overflow-x-hidden" v-else="">
            <v-simple-table light>
              <tbody>
                <tr>
                  <td style="width:100%">Ingen begär ordet</td>
                </tr>
              </tbody>
            </v-simple-table>
          </div>
        </div>
      </div>
    </div>

    <div class="meeting-layout-bottom"></div>
  </div>
</template>

<script>
import Agenda from "@/components/screen/Agenda.vue";
import SpeakerTime from "@/components/meetings/SpeakerTime.vue";

const collator = new Intl.Collator("en", {
  numeric: true,
  sensitivity: "base"
});

var textAddPayment = "Lägg till betalningsmetod";

export default {
  components: {
    Agenda,
    SpeakerTime
  },
  data() {
    return {
      visiblePoints: [],
      stateItems: [],
      dropdownState: 0
    };
  },
  computed: {
    state() {
      return this.getState(this.meeting);
    },
    stateColor() {
      return "success";
    },
    meeting() {
      return this.$store.getters["meetings/getMeeting"](
        this.$route.params.meetingId
      );
    },
    activeSpeakerId() {
      const s = this.$store.getters["meetings/getState"];
      return s;
    },
    decisionPoints() {
      const points = this.$store.getters["meetings/getDecisionPoints"];

      return points
        .filter(point => {
          return point.opened;
        })
        .sort((a, b) => {
          return b.opened.toDate().getTime() - a.opened.toDate().getTime();
        });
    },
    ballotsAndMandates() {
      var electors = {};
      var delegations = {};
      var ballots = this.meeting.ballots;
      var mandates = this.$store.getters["meetings/getMandates"];

      /* electors will look like this
      {
        yyyymmddxxxx: {
          ssn: yyyymmddxxxx,
          ballots: [ballotId],
          present: true/false,
          delegatedTo: yyyymmddxxxx
        }
      }
      */

      // Create base list of ssn and ballots
      Object.keys(ballots).forEach(function(ballot) {
        var ssns = ballots[ballot].ssn;
        ssns.forEach(ssn => {
          if (electors[ssn] == null) {
            electors[ssn] = { ssn: ssn, ballots: [] };
          }
          electors[ssn].ballots.push(ballot);
        });
      });

      // Decorate items with delegatedTo
      mandates.forEach(mandate => {
        if (mandate.ssn != mandate.id) {
          var elector = electors[mandate.id];
          elector.delegatedTo = mandate.ssn;
          if (delegations[mandate.ssn] == null) {
            delegations[mandate.ssn] = [];
          }
          delegations[mandate.ssn].push(elector);
        }
      });

      // Set presence for any present elector
      mandates.forEach(mandate => {
        if (mandate.ssn == mandate.id && mandate.presence != null) {
          var elector = electors[mandate.id];

          if (elector != null) {
            elector.present = true;

            Object.values(electors).forEach(dElector => {
              if (dElector.delegatedTo == mandate.id) {
                dElector.present = true;
              }
            });
          }
        }
      });

      // Return an array of electors
      return Object.values(electors)
        .sort((a, b) => {
          var aBallots = a.ballots.sort((j, k) => j.localeCompare(k)).join(",");
          var bBallots = b.ballots.sort((j, k) => j.localeCompare(k)).join(",");

          return aBallots.localeCompare(bBallots);
        })
        .sort((a, b) => {
          if (a.present == b.present) {
            return 0;
          } else if (a.present == true && b.present != true) {
            return -1;
          } else if (a.present == false && b.present != false) {
            return 1;
          }
        });
    },
    mandates() {
      return this.$store.getters["meetings/getMandates"];
    },
    speakers() {
      var electors = this.$store.getters["meetings/getElectors"];
      if (this.meeting.askToSpeak) {
        const result = electors
          .filter(
            elector =>
              elector.askToSpeak &&
              elector.id !== this.activeSpeakerId.speakerId
          )
          .sort((a, b) => {
            // equal items sort equally
            if (a.askToSpeak === b.askToSpeak) {
              return 0;
              // nulls sort after anything else
            } else {
              return a.askToSpeak < b.askToSpeak ? -1 : 1;
            }
          })
          .sort(a => {
            if (a.id === this.activeSpeakerId.speakerId) {
              return -1;
            } else {
              return 1;
            }
          });
        return result;
      } else {
        return [];
      }
    },
    currentSpeaker() {
      if (this.meeting.askToSpeak) {
        const electors = this.$store.getters["meetings/getElectors"];
        return (
          electors.find(
            elector => elector.id === this.activeSpeakerId.speakerId
          ) || null
        );
      }
      return null;
    },
    hasActiveSpeaker() {
      return !!this.activeSpeakerId.speakerId;
    },
    activeSpeakerDisplayName() {
      if (this.currentSpeaker && this.currentSpeaker.name) {
        return this.currentSpeaker.name;
      }
      return "Ordförande";
    },
    electors() {
      var electors = this.$store.getters["meetings/getElectors"];
      if (this.meeting.askToSpeak) {
        electors = electors
          .sort((a, b) => {
            return collator.compare(a.ballots[0], b.ballots[0]);
          })
          .sort((a, b) => {
            // equal items sort equally
            if (a.askToSpeak === b.askToSpeak) {
              return 0;
            }
            // nulls sort after anything else
            else if (a.askToSpeak === null || a.askToSpeak === undefined) {
              return 1;
            } else if (b.askToSpeak === null || b.askToSpeak === undefined) {
              return -1;
            } else {
              return a.askToSpeak < b.askToSpeak ? -1 : 1;
            }
          })
          .sort(a => {
            if (a.id === this.activeSpeakerId.speakerId) {
              return -1;
            } else {
              return 1;
            }
          });
      }

      return electors;
    },

    totalNumberOfBallots() {
      const ballots = this.meeting.ballots;

      return Object.keys(ballots).length;
    },

    totalNumberOfMembers() {
      const ballots = this.meeting.ballots;
      const members = Object.values(ballots);
      let individuals = [];

      members.forEach(ballotIndividuals => {
        individuals = individuals.concat(ballotIndividuals.ssn);
      });

      return individuals.length;
    },

    totalNumberOfPresentMembers() {
      const electors = this.$store.getters["meetings/getElectors"];

      return electors.length;
    },

    totalNumberOfPresentBallots() {
      const electors = this.$store.getters["meetings/getElectors"];
      var ballotsList = {};

      electors.forEach(elector => {
        elector.ballots.forEach(ballot => {
          ballotsList[ballot] = true;
        });
      });

      return Object.keys(ballotsList).length;
    },
    totalNumberOfPresentMandatedBallots() {
      const electors = this.$store.getters["meetings/getElectors"];
      var ballotsList = {};

      electors.forEach(elector => {
        elector.delegated.forEach(delegation => {
          delegation.ballots.forEach(ballot => {
            ballotsList[ballot] = true;
          });
        });
      });

      return Object.keys(ballotsList).length;
    },

    isOpen() {
      return this.meeting.adminOpened && !this.meeting.closed;
    },
    numberOfDelegates() {
      var delegates = 0;
      var electors = this.$store.getters["meetings/getElectors"];

      electors.forEach(elector => {
        delegates += elector.delegated.length;
      });

      return delegates;
    }
  },
  watch: {
    meeting(meeting) {
      this.setState(meeting);
    }
  },
  methods: {
    sortedResults(results) {
      var r = Array.from(results);
      return r.sort((a, b) => b.result - a.result);
    },
    async downloadReport() {
      const url = await this.$store.dispatch(
        "meetings/downloadReport",
        this.meeting.id
      );
      window.open(url);
    },
    goToOrganization() {
      this.$router.push({
        name: "organization",
        params: { orgId: this.meeting.organizationId }
      });
    },
    activatePoint(item) {
      const decisionPoint = {
        meetingId: this.meeting.id,
        id: item.id
      };
      this.$store.dispatch("meetings/activateDecisionPoint", decisionPoint);
    },
    deactivatePoint(item) {
      const decisionPoint = {
        meetingId: this.meeting.id,
        id: item.id
      };
      this.$store.dispatch("meetings/deactivateDecisionPoint", decisionPoint);
    },
    getState(meeting) {
      const published = !!meeting.published;
      const inlet = !!meeting.inlet;
      const open = !!meeting.adminOpened;
      const closed = !!meeting.closed;

      if (closed) {
        return 4;
      }

      if (open) {
        return 3;
      }

      if (inlet) {
        return 2;
      }

      if (published) {
        return 1;
      }

      return 0;
    },
    changeState(state) {
      switch (state) {
        case 1:
          if (!this.meeting.published) {
            if (
              Object.keys(this.meeting.ballots).length <= 5 ||
              this.meeting.contractId ||
              this.meeting.stripePaymentMethod
            ) {
              this.$root.$emit("approvePublish");
            } else {
              this.addPaymentSnackbar();
            }
          }
          break;
        case 2:
          this.$store.dispatch("meetings/openMeetingInlet", this.meeting.id);
          break;
        case 3:
          if (
            Object.keys(this.meeting.ballots).length <= 5 ||
            this.meeting.contractId ||
            this.meeting.stripePaymentMethod
          ) {
            this.$store.dispatch("meetings/openMeeting", this.meeting.id);
          } else {
            this.addPaymentSnackbar();
          }
          break;
        case 4:
          this.$store.dispatch("meetings/closeMeeting", this.meeting.id);
          break;
      }
    },
    setState(meeting) {
      if (!meeting) {
        return;
      }
      const state = this.getState(meeting);
      this.dropdownState = state;
      const items = [];

      const unpublished = state > 0 ? null : "Opublicerat möte";
      if (unpublished) items.push({ text: unpublished, value: 0 });

      const published = meeting.published
        ? "1. Möte publicerat"
        : "1. Publicera möte";
      const ballots = Object.keys(meeting.ballots || {});
      const paid = meeting.isPaidFor;
      const hasContract = !!meeting.contractId;
      const isPublished = !!meeting.published;
      const hasStripe = !!meeting.stripePaymentMethod;

      items.push({
        text: published,
        value: 1,
        disabled:
          state > 2 ||
          (!hasContract &&
            !isPublished &&
            ballots.length > 5 &&
            !paid &&
            !hasStripe)
      });
      const inlet = meeting.inlet
        ? "2. Närvaroregistrering öppnad"
        : "2. Öppna närvaroregistrering";
      items.push({ text: inlet, value: 2, disabled: state > 3 || state < 1 });
      const open = meeting.adminOpened ? "3. Möte öppnat" : "3. Öppna möte";
      items.push({ text: open, value: 3, disabled: state > 4 || state < 2 });
      const closed = meeting.closed ? "4. Möte stängt" : "4. Stäng möte";
      items.push({ text: closed, value: 4, disabled: state < 3 });

      this.stateItems = items;

      if (meeting.error && meeting.error == "noPaymentMethod") {
        this.addPaymentSnackbar();
        this.$store.dispatch("meetings/clearError", meeting.id);
      }
    },
    translateResult(result) {
      switch (result) {
        case "no":
          return "Nej";
        case "yes":
          return "Ja";
        case "":
          return "Ej röstat";
        default:
          return result;
      }
    },
    isVisible(id) {
      return this.visiblePoints.includes(id);
    },
    toggleVisible(id) {
      if (this.isVisible(id)) {
        this.visiblePoints = this.visiblePoints.filter(el => el !== id);
      } else {
        this.visiblePoints.push(id);
      }
    },
    addPaymentSnackbar() {
      this.$store.commit("snackbar/push", {
        text: textAddPayment,
        icon: "mdi-credit-card-check-outline",
        accept: () => {
          this.$root.$emit("missingPaymentMetod");
        },
        deny: () => {}
      });
    },
    openStatusInformationModal() {
      this.$root.$emit("openStatusInformationModal");
    },
    activeSpeaker(elector) {
      this.$store.dispatch("meetings/updateSpeaker", {
        meetingId: this.meeting.id,
        elector
      });
    }
  },
  async mounted() {
    await this.$store.dispatch(
      "meetings/stateListener",
      this.$route.params.meetingId
    );
    await this.$store.dispatch(
      "meetings/decisionListener",
      this.$route.params.meetingId
    );
    await this.$store.dispatch(
      "meetings/mandatesListener",
      this.$route.params.meetingId
    );
    await this.$store.dispatch(
      "meetings/electorsListener",
      this.$route.params.meetingId
    );
    this.setState(this.meeting);
    this.$root.$on("setStateMeeting", () => {
      this.dropdownState = 0;
    });
  }
};
</script>

<style>
.meeting-container {
  height: 100vh;
  display: grid;
  grid-template-columns: 1fr 1fr min-content;
  grid-template-rows: min-content min-content 1fr 0px;
  gap: 20px 20px;
  grid-template-areas:
    "meeting-layout-top meeting-layout-top meeting-layout-top"
    "meeting-left-top meeting-middle-top meeting-right"
    "meeting-left-content meeting-middle-content meeting-right"
    "meeting-layout-bottom meeting-layout-bottom meeting-layout-bottom";
}

.meeting-layout-top {
  padding-left: 16px;
  padding-right: 16px;
  padding-top: 8px;
  padding-bottom: 8px;
  background: #002435;
  -webkit-box-shadow: 0px 10px 5px 0px rgba(0, 0, 0, 0.25);
  -moz-box-shadow: 0px 10px 5px 0px rgba(0, 0, 0, 0.25);
  box-shadow: 0px 10px 5px 0px rgba(0, 0, 0, 0.25);
  grid-area: meeting-layout-top;
}
.meeting-layout-bottom {
  grid-area: meeting-layout-bottom;
}

.meeting-left-top {
  padding-left: 16px;
  grid-area: meeting-left-top;
}

.meeting-middle-top {
  grid-area: meeting-middle-top;
}

.meeting-right {
  padding-right: 16px;
  grid-area: meeting-right;
  height: 100%;
}

.active-speaker-name {
  font-size: 1.1rem;
  font-weight: 600;
}

.meeting-left-content {
  padding-left: 16px;
  padding-bottom: 16px;
  grid-area: meeting-left-content;
  height: 100%;
  overflow-y: scroll;
  overflow-x: hidden;
}

.meeting-middle-content {
  grid-area: meeting-middle-content;
  height: 100%;
  overflow-y: scroll;
  overflow-x: hidden;
}

.meeting-top-container {
  display: grid;
  grid-template-columns: 36px auto 1fr auto auto auto;
  grid-template-rows: 1fr;
  gap: 0px 0px;
  grid-template-areas: "meeting-top-logo meeting-top-back meeting-top-left meeting-top-middle meeting-top-right meeting-top-usermenu";
}

.meeting-top-logo {
  padding-top: 7px;
  grid-area: meeting-top-logo;
  margin-right: 12px;
  align-self: end;
}

.meeting-top-back {
  grid-area: meeting-top-back;
}

.meeting-top-left {
  margin-left: 12px;
  align-self: center;
  grid-area: meeting-top-left;
}

.meeting-top-middle {
  grid-area: meeting-top-middle;
}

.meeting-top-right {
  grid-area: meeting-top-right;
}

.meeting-top-usermenu {
  grid-area: meeting-top-usermenu;
}

.row-container {
  display: grid;
  grid-template-columns: 1fr min-content;
  grid-template-rows: 1fr;
  gap: 0 0px;
  grid-template-areas: "row-left row-icon";
}

.row-left {
  grid-area: row-left;
}

.row-icon {
  grid-area: row-icon;
}

.decision-container {
  display: grid;
  grid-template-columns: min-content 1fr min-content;
  grid-template-rows: min-content min-content;
  gap: 0px 0px;
  grid-template-areas:
    "decision-icon decision-text decision-edit"
    "decision-icon decision-text decision-unfold";
}

.decision-icon {
  grid-area: decision-icon;
  padding-left: 16px;
  padding-top: 16px;
  padding-bottom: 16px;
}

.decision-text {
  grid-area: decision-text;
  padding-top: 16px;
  padding-bottom: 16px;
}

.decision-edit {
  grid-area: decision-edit;
}

.decision-unfold {
  grid-area: decision-unfold;
  text-align: right;
  vertical-align: bottom;
  align-self: end;
}

.icon-button-container {
  display: grid;
  grid-template-columns: 1fr auto;
  grid-template-rows: 1fr;
  gap: 0px 0px;
  grid-template-areas: "icon-button-text icon-button-icon";
}
.icon-button-text {
  grid-area: icon-button-text;
  align-self: self-end;
}
.icon-button-icon {
  grid-area: icon-button-icon;
}
</style>
